import Keycloak from 'keycloak-js';
import store from '../vuex/store';
import { getEnv } from '../environment';

export default class KeycloakAuthService {
  constructor() {
    this.login = this.login.bind(this);
    this.loginLevel4 = this.loginLevel4.bind(this);
    this.logout = this.logout.bind(this);

    this.onAuthSuccess = this.onAuthSuccess.bind(this);
    this.onAuthError = this.onAuthError.bind(this);
    this.onAuthRefreshError = this.onAuthRefreshError.bind(this);
    this.onAuthRefreshSuccess = this.onAuthRefreshSuccess.bind(this);
    this.updateToken = this.updateToken.bind(this);

    this.keycloak = new Keycloak({
      url: getEnv('VUE_APP_KEYCLOAK_URL'),
      realm: getEnv('VUE_APP_KEYCLOAK_REALM'),
      clientId: getEnv('VUE_APP_KEYCLOAK_CLIENT_ID'),
    });

    this.keycloak.onAuthSuccess = this.onAuthSuccess;
    this.keycloak.onAuthError = this.onAuthError;
    this.keycloak.onAuthRefreshSuccess = this.onAuthRefreshSuccess;
    this.keycloak.onAuthRefreshError = this.onAuthRefreshError;
  }

  init() {
    return this.keycloak
      .init({ onLoad: 'login-required' })
      .then(async () => {
        await store.dispatch('representasjoner/getRepresentations');
        return Promise.resolve();
      })
      .catch(() => Promise.reject());
  }

  login() {
    this.keycloak.login();
  }

  loginLevel4() {
    this.keycloak.logout({ redirectUri: `${this.keycloak.createLoginUrl()}&acr_values=Level4` });
  }

  logout() {
    store.dispatch('representasjoner/removeStorageItems').then(() => {
      this.keycloak.logout({ redirectUri: getEnv('VUE_APP_KEYCLOAK_LOGOUT_URL') });
    });
  }

  static capitalizeFirstLetterOfNames(names) {
    return names
      .toLowerCase()
      .split(' ')
      .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
      .join(' ');
  }

  static securityLevelFromAcr(acrSecurityLevel) {
    return Number(acrSecurityLevel.charAt(acrSecurityLevel.length - 1));
  }

  static securityLevelIsValid(idTokenParsed) {
    const validSecurityLevels = ['Level3', 'Level4', 'idporten-loa-substantial', 'idporten-loa-high'];

    return idTokenParsed.acr_security_level && validSecurityLevels.includes(idTokenParsed.acr_security_level);
  }

  static idTokenIsValid(idTokenParsed) {
    return (
      idTokenParsed.pid &&
      idTokenParsed.given_name &&
      idTokenParsed.family_name &&
      KeycloakAuthService.securityLevelIsValid(idTokenParsed)
    );
  }

  updateAccessTokenAndUserinfo() {
    store.dispatch('setAccessToken', this.keycloak.token);
    store.dispatch('setLoginLevel4Function', this.loginLevel4);

    if (KeycloakAuthService.idTokenIsValid(this.keycloak.idTokenParsed)) {
      store.dispatch('setBrukerinfo', {
        brukerId: this.keycloak.idTokenParsed.pid,
        fornavn: KeycloakAuthService.capitalizeFirstLetterOfNames(this.keycloak.idTokenParsed.given_name),
        etternavn: KeycloakAuthService.capitalizeFirstLetterOfNames(this.keycloak.idTokenParsed.family_name),
        sikkerhetsniva: KeycloakAuthService.securityLevelFromAcr(this.keycloak.idTokenParsed.acr_security_level),
        email: this.keycloak.idTokenParsed.email,
      });
    } else {
      store.dispatch('addGlobalMessage', {
        messageType: 'error',
        message: 'Vi har for øyeblikket problemer. Vennligst prøv igjen senere.',
      });
    }
  }

  onAuthSuccess() {
    this.updateAccessTokenAndUserinfo();
    store.dispatch('setAuthenticated', true);
  }

  onAuthError() {
    this.logout();
  }

  onAuthRefreshSuccess() {
    store.dispatch('setAccessToken', this.keycloak.token);
  }

  onAuthRefreshError() {
    this.logout();
  }

  /**
   * Updates token if possible. Sets new accessToken in Vuex.
   * @param minValidity: seconds left on token validity before we try to refresh the token
   * @returns {Promise<any>}
   */
  updateToken(minValidity = 60) {
    return this.keycloak
      .updateToken(minValidity)
      .then((refreshed) => {
        if (refreshed) {
          store.dispatch('setAccessToken', this.keycloak.token);
        }
        return Promise.resolve(true);
      })
      .catch(() => {
        this.logout();
        return Promise.reject();
      });
  }
}
